import {
  IAiConversation,
  IAiMessage,
  IAiMessageImage,
  IAiPendingTurn,
  IAiSupportDraft,
  IAiSupportTicket,
  aiAssistantTexts,
} from "../models/aiAssistantModels";

/**
 * What the chat shows, in order: the stored conversation, then the turn being sent right now
 * until the stored conversation covers it. A turn's rows keep one key from the first frame to the
 * stored copy (the stored question and answer take over the turn's keys), so nothing re-mounts and
 * the entry animation plays once. Only the turn's own rows (`live`) animate; a loaded conversation
 * appears as it is.
 */
export type AiChatEntry = { live?: boolean } & (
  | { kind: "user"; key: string; text: string; images: IAiMessageImage[] }
  | { kind: "assistant"; key: string; text: string; isStreaming: boolean }
  | { kind: "status"; key: string; toolLabel: string | null }
  | { kind: "error"; key: string; text: string; canRetry: boolean }
  | { kind: "notice"; key: string; text: string }
  | {
      kind: "draft";
      key: string;
      messageId: number | null;
      draft: IAiSupportDraft;
      supportChatId: string | null;
    }
  | { kind: "ticket"; key: string; ticket: IAiSupportTicket }
);

export interface IAiRetryTarget {
  text: string;
  images: IAiMessageImage[];
}

const isActive = (turn: IAiPendingTurn) => turn.phase === "waiting" || turn.phase === "streaming";

/** The stored conversation already holds this turn's question and an answer (or error) after it. */
export const isTurnCovered = (turn: IAiPendingTurn, conversation: IAiConversation | null): boolean => {
  if (!conversation || isActive(turn)) return false;
  if (conversation.id !== turn.conversationId) return false;
  // The stored answer is there ("done" told its id).
  if (turn.answerMessageId !== null && conversation.messages.some((message) => message.id === turn.answerMessageId)) {
    return true;
  }
  if (turn.userMessageId === null) return false;
  const userIndex = conversation.messages.findIndex((message) => message.id === turn.userMessageId);
  if (userIndex === -1) return false;
  return conversation.messages.slice(userIndex + 1).some((message) => message.role === "assistant");
};

const messageKey = (id: number) => `m-${id}`;
const turnUserKey = (turn: IAiPendingTurn) => `${turn.key}-user`;
const turnAnswerKey = (turn: IAiPendingTurn) => `${turn.key}-answer`;

/** The stored question and answer of the current turn keep the keys the turn showed them under. */
const turnKeyAliases = (turn: IAiPendingTurn | null): Map<number, string> => {
  const aliases = new Map<number, string>();
  if (turn?.userMessageId != null) aliases.set(turn.userMessageId, turnUserKey(turn));
  if (turn?.answerMessageId != null) aliases.set(turn.answerMessageId, turnAnswerKey(turn));
  return aliases;
};

/** The small card of a ticket the assistant created, right under its answer (or error row). */
const ticketEntries = (answerKey: string, ticket: IAiSupportTicket | null): AiChatEntry[] =>
  ticket ? [{ kind: "ticket", key: `${answerKey}-ticket`, ticket }] : [];

const storedEntries = (messages: IAiMessage[], aliases: Map<number, string>): AiChatEntry[] =>
  messages.flatMap<AiChatEntry>((message) => {
    const key = aliases.get(message.id) ?? messageKey(message.id);
    if (message.role === "user") {
      return [{ kind: "user", key, text: message.text, images: message.images }];
    }
    if (message.isError) {
      return [
        { kind: "error", key, text: message.text || aiAssistantTexts.genericError, canRetry: false },
        ...ticketEntries(key, message.supportTicket),
      ];
    }
    const entries: AiChatEntry[] = [];
    if (message.text) {
      entries.push({ kind: "assistant", key, text: message.text, isStreaming: false });
    }
    if (message.supportDraft) {
      entries.push({
        kind: "draft",
        key: `${key}-draft`,
        messageId: message.id,
        draft: message.supportDraft,
        supportChatId: message.supportChatId,
      });
    }
    entries.push(...ticketEntries(key, message.supportTicket));
    return entries;
  });

const turnEntries = (turn: IAiPendingTurn, storedIds: Set<number>): AiChatEntry[] => {
  const entries: AiChatEntry[] = [];
  const answerKey = turnAnswerKey(turn);
  // A refetch may bring the stored answer (with its card) before the turn ends; show the card once.
  const ticket = turn.answerMessageId !== null && storedIds.has(turn.answerMessageId) ? null : turn.supportTicket;

  if (turn.userMessageId === null || !storedIds.has(turn.userMessageId)) {
    entries.push({ kind: "user", key: turnUserKey(turn), text: turn.text, images: turn.images });
  }

  if (turn.phase === "error") {
    entries.push({
      kind: "error",
      key: answerKey,
      text: turn.errorMessage || aiAssistantTexts.genericError,
      canRetry: false,
    });
    // The ticket was created before the turn failed: it exists, so its card stays.
    entries.push(...ticketEntries(answerKey, ticket));
    return entries;
  }

  if (turn.answerText) {
    entries.push({ kind: "assistant", key: answerKey, text: turn.answerText, isStreaming: isActive(turn) });
  }

  // The typing dots before the first word, and the running tool's label while it works.
  if (isActive(turn) && (!turn.answerText || turn.toolLabel)) {
    entries.push({ kind: "status", key: `${turn.key}-status`, toolLabel: turn.toolLabel });
  }

  if (turn.supportDraft) {
    entries.push({
      kind: "draft",
      key: `${answerKey}-draft`,
      messageId: turn.answerMessageId,
      draft: turn.supportDraft,
      supportChatId: null,
    });
  }

  entries.push(...ticketEntries(answerKey, ticket));

  if (turn.phase === "stopped" && turn.errorMessage) {
    entries.push({ kind: "notice", key: `${turn.key}-notice`, text: turn.errorMessage });
  }

  return entries.map((entry) => ({ ...entry, live: true }));
};

export const buildChatEntries = (
  conversation: IAiConversation | null,
  turn: IAiPendingTurn | null,
  canRetry: boolean,
): AiChatEntry[] => {
  const messages = conversation?.messages ?? [];
  const entries = storedEntries(messages, turnKeyAliases(turn));

  if (turn && !isTurnCovered(turn, conversation)) {
    const storedIds = new Set(messages.map((message) => message.id));
    entries.push(...turnEntries(turn, storedIds));
  }

  // Only the newest error offers "Опитай отново": it resends the question right before it. An error
  // with a ticket card after it offers none: the resent question could create a second ticket.
  const last = entries[entries.length - 1];
  if (canRetry && last?.kind === "error") {
    entries[entries.length - 1] = { ...last, canRetry: true };
  }

  return entries;
};

/** The question to resend for the error at the end of the chat. */
export const findRetryTarget = (
  conversation: IAiConversation | null,
  turn: IAiPendingTurn | null,
): IAiRetryTarget | null => {
  if (turn && turn.phase === "error" && !isTurnCovered(turn, conversation)) {
    return { text: turn.text, images: turn.images };
  }
  const messages = conversation?.messages ?? [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === "user") {
      return { text: messages[index].text, images: messages[index].images };
    }
  }
  return null;
};
